import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/utils";

interface DynamicSelectFilterProps<T> {
  placeholder: string;
  data: T[];
  value?: string;
  onChange: (value: string) => void;
  valueExtractor: (item: T) => string;
  labelExtractor: (item: T) => string;
  className?: string;
  searchPlaceholder?: string;
}

export function DynamicSelectFilter<T>({
  placeholder,
  data,
  value,
  onChange,
  valueExtractor,
  labelExtractor,
  className,
  searchPlaceholder = "Поиск...",
}: DynamicSelectFilterProps<T>) {
  const [open, setOpen] = React.useState(false);

  const selectedLabel = value
    ? labelExtractor(data.find((item) => valueExtractor(item) === value)!)
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-[180px] h-10 bg-white justify-between font-normal",
            className
          )}
        >
          <span
            className={cn(
              "truncate",
              !selectedLabel && "text-muted-foreground"
            )}
          >
            {selectedLabel ?? placeholder}
          </span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>Ничего не найдено</CommandEmpty>
            <CommandGroup>
              {/* Сброс */}
              <CommandItem
                value="all"
                onSelect={() => {
                  onChange("");
                  setOpen(false);
                }}
                className="cursor-pointer hover:bg-accent"
              >
                <Check
                  size={14}
                  className={cn("mr-2", !value ? "opacity-100" : "opacity-0")}
                />
                Все
              </CommandItem>

              {data.map((item, index) => {
                const itemValue = valueExtractor(item);
                const itemLabel = labelExtractor(item);

                return (
                  <CommandItem
                    key={`${itemValue}-${index}`}
                    value={itemLabel} // поиск идёт по value в Command, поэтому передаём label
                    onSelect={() => {
                      onChange(itemValue === value ? "" : itemValue);
                      setOpen(false);
                    }}
                    className="cursor-pointer hover:bg-accent"
                  >
                    <Check
                      size={14}
                      className={cn(
                        "mr-2",
                        value === itemValue ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {itemLabel}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
